import {
  Box,
  Button,
  Group,
  Popover,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core'
import { ChartBarIcon, MedalIcon, StarIcon, TrophyIcon } from '@phosphor-icons/react'
import { useCallback, useMemo, useState, type ReactNode } from 'react'

import type { BoggleAchievementCategory } from '~/boggle/config/achievements/types'
import type {
  BoggleKnownAchievement,
  BoggleKnownAchievementsView,
} from '~/boggle/lib/achievements/knownAchievements'
import type { BoggleUnviewedAchievementTier } from '~/boggle/progress/types'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import { RatingBadge } from '~/boggle/ui/ratings/RatingBadge'

import {
  formatAchievementCompleteLine,
  formatAchievementNearMiss,
  formatAchievementProgress,
  formatAchievementProgressLine,
  getActiveAchievementTier,
  isDiscreteRatingAchievement,
  shouldShowTierPips,
} from './achievementPresentation'
import classes from './Achievements.module.css'

const CATEGORY_LABELS: Record<BoggleAchievementCategory, string> = {
  'badge-album': 'Badge Album',
  career: 'Career',
  feats: 'Feats',
  ratings: 'Ratings',
}

const CATEGORY_ICONS: Record<BoggleAchievementCategory, typeof TrophyIcon> = {
  'badge-album': TrophyIcon,
  career: ChartBarIcon,
  feats: StarIcon,
  ratings: MedalIcon,
}

const CATEGORY_ORDER: readonly BoggleAchievementCategory[] = [
  'career',
  'ratings',
  'badge-album',
  'feats',
]

const BADGE_ALBUM_LINK_IDS = new Set(['badge-breakthrough', 'common-album'])

function AchievementPopover({
  achievement,
  children,
  opened,
  onOpenChange,
  unviewedTierIds,
}: {
  achievement: BoggleKnownAchievement
  children: ReactNode
  opened: boolean
  onOpenChange: (opened: boolean) => void
  unviewedTierIds: ReadonlySet<string>
}) {
  return (
    <Popover
      onChange={onOpenChange}
      opened={opened}
      position="top"
      shadow="md"
      transitionProps={{ duration: 120 }}
      offset={6}
      withinPortal
    >
      <Popover.Target>
        <UnstyledButton
          aria-expanded={opened}
          aria-label={`Show details for ${achievement.definition.title}`}
          className={classes.trigger}
          data-testid={`achievement-popover-trigger-${achievement.definition.id}`}
          onClick={() => onOpenChange(!opened)}
        >
          {children}
        </UnstyledButton>
      </Popover.Target>
      {opened ? (
        <Popover.Dropdown
          bd="1px solid rgba(255, 214, 102, 0.36)"
          bg="rgba(20, 18, 14, 0.96)"
          className={classes.popover}
          px={14}
          py={12}
        >
          <Stack
            gap={10}
            maw={300}
            data-testid={`achievement-popover-${achievement.definition.id}`}
          >
            <Text c="white" ff="Tilt Neon, sans-serif" fz={20} fw={600} lh={1.12}>
              {achievement.definition.title}
            </Text>
            <Stack gap={7}>
              {achievement.tiers.map(tier => (
                <Stack className={classes.popoverTier} gap={3} key={tier.definition.id}>
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap={6} wrap="nowrap">
                      <Text c="rgba(252,247,235,0.94)" fz={12} fw={700}>
                        {tier.definition.label}
                      </Text>
                      {unviewedTierIds.has(tier.definition.id) ? (
                        <Text className={classes.newPill}>New</Text>
                      ) : null}
                    </Group>
                    <Text
                      c={
                        tier.completed
                          ? 'var(--mantine-color-yellow-4)'
                          : 'rgba(232,224,210,0.56)'
                      }
                      fz={11}
                      fw={700}
                    >
                      {tier.completed
                        ? 'Done'
                        : formatAchievementProgress(
                            tier.progress.current,
                            tier.progress.target,
                          )}
                    </Text>
                  </Group>
                  <Text c="rgba(232,224,210,0.72)" fz={12} lh={1.35}>
                    {tier.definition.description}
                  </Text>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Popover.Dropdown>
      ) : null}
    </Popover>
  )
}

export function Achievements({
  failed,
  loading,
  onViewBadges,
  sessionUnviewedAchievementTiers = [],
  view,
}: {
  failed: boolean
  loading: boolean
  onViewBadges?: () => void
  sessionUnviewedAchievementTiers?: readonly BoggleUnviewedAchievementTier[]
  view: BoggleKnownAchievementsView
}) {
  const [selectedAchievementId, setSelectedAchievementId] = useState<string | null>(null)
  const unviewedTierIds = useMemo(
    () => new Set(sessionUnviewedAchievementTiers.map(entry => entry.tierId)),
    [sessionUnviewedAchievementTiers],
  )
  const byCategory = new Map(
    CATEGORY_ORDER.map(category => [
      category,
      view.achievements.filter(
        achievement => achievement.definition.category === category,
      ),
    ]),
  )

  const toggleAchievement = useCallback((achievementId: string, opened: boolean) => {
    setSelectedAchievementId(current => {
      if (!opened) {
        return current === achievementId ? null : current
      }
      return achievementId
    })
  }, [])

  return (
    <Stack className={classes.root} gap={0}>
      <Group className={classes.summary} justify="space-between" wrap="nowrap">
        <Group gap={3} c="rgba(232,224,210,0.72)" align="baseline">
          <Text c="white" data-testid="achievements-completed-count" fz={18} lh={1}>
            {view.completedTierCount}
          </Text>
          <Text fz={15} lh={1}>
            /{view.totalTierCount}
          </Text>
        </Group>
      </Group>

      <ScrollArea className={classes.root} offsetScrollbars>
        <Stack className={classes.viewport} gap={12}>
          {loading ? (
            <Text c="dimmed" fz={12}>
              Loading
            </Text>
          ) : null}
          {failed ? (
            <Text c="red.3" fz={12}>
              Achievements unavailable
            </Text>
          ) : null}
          {!loading && !failed && view.achievements.length === 0 ? (
            <Text c="dimmed" fz={12}>
              No progress yet
            </Text>
          ) : null}

          {CATEGORY_ORDER.map(category => {
            const achievements = byCategory.get(category) ?? []
            if (achievements.length === 0) {
              return null
            }

            return (
              <Stack gap={7} key={category}>
                <Text className={classes.sectionTitle}>{CATEGORY_LABELS[category]}</Text>
                <Stack gap={8}>
                  {achievements.map(achievement => {
                    const activeTier = getActiveAchievementTier(achievement)
                    const progress = activeTier?.progress
                    const percent = progress
                      ? Math.min(100, (progress.current / progress.target) * 100)
                      : 0
                    const complete =
                      achievement.totalTierCount > 0 &&
                      achievement.completedTierCount === achievement.totalTierCount
                    const hasUnviewed = achievement.tiers.some(tier =>
                      unviewedTierIds.has(tier.definition.id),
                    )
                    const CategoryIcon = CATEGORY_ICONS[achievement.definition.category]
                    const showBar =
                      activeTier !== null &&
                      !complete &&
                      !isDiscreteRatingAchievement(achievement)
                    const progressLine = isDiscreteRatingAchievement(achievement)
                      ? `${achievement.completedTierCount} / ${achievement.totalTierCount} ratings`
                      : activeTier && !complete
                        ? formatAchievementProgressLine({ achievement, tier: activeTier })
                        : formatAchievementCompleteLine(achievement)
                    const nearMiss =
                      activeTier && !complete
                        ? formatAchievementNearMiss({ achievement, tier: activeTier })
                        : null

                    return (
                      <Stack
                        className={classes.achievement}
                        data-complete={complete}
                        data-testid={`boggle-achievement-${achievement.definition.id}`}
                        data-unviewed={hasUnviewed}
                        gap={8}
                        key={achievement.definition.id}
                      >
                        <AchievementPopover
                          achievement={achievement}
                          opened={selectedAchievementId === achievement.definition.id}
                          onOpenChange={opened =>
                            toggleAchievement(achievement.definition.id, opened)
                          }
                          unviewedTierIds={unviewedTierIds}
                        >
                          <Stack gap={8}>
                            <Group
                              align="flex-start"
                              gap={9}
                              justify="space-between"
                              wrap="nowrap"
                            >
                              <Group align="flex-start" gap={9} wrap="nowrap">
                                <ThemeIcon
                                  className={classes.categoryIcon}
                                  radius={8}
                                  variant="transparent"
                                >
                                  <IconGlyph
                                    icon={CategoryIcon}
                                    size={18}
                                    weight="duotone"
                                  />
                                </ThemeIcon>
                                <Box>
                                  <Group gap={6} wrap="nowrap">
                                    <Text c="rgba(252,247,235,0.94)" fz={13} fw={700}>
                                      {achievement.definition.title}
                                    </Text>
                                    {hasUnviewed ? (
                                      <Text className={classes.newPill}>New</Text>
                                    ) : null}
                                  </Group>
                                  <Text c="rgba(232,224,210,0.68)" fz={11}>
                                    {progressLine}
                                  </Text>
                                </Box>
                              </Group>
                            </Group>

                            {isDiscreteRatingAchievement(achievement) ? (
                              <Group gap={6} wrap="wrap">
                                {achievement.tiers.map(tier => (
                                  <Group
                                    className={classes.ratingPip}
                                    data-completed={tier.completed}
                                    data-unviewed={unviewedTierIds.has(
                                      tier.definition.id,
                                    )}
                                    gap={4}
                                    key={tier.definition.id}
                                    wrap="nowrap"
                                  >
                                    {tier.definition.requirement.type ===
                                    'reached_rating' ? (
                                      <RatingBadge
                                        h={16}
                                        ratingId={tier.definition.requirement.ratingId}
                                        w={16}
                                      />
                                    ) : null}
                                    <Text fz={10} fw={700}>
                                      {tier.definition.label}
                                    </Text>
                                  </Group>
                                ))}
                              </Group>
                            ) : null}

                            {shouldShowTierPips(achievement) ? (
                              <Group gap={5} wrap="wrap">
                                {achievement.tiers.map(tier => (
                                  <Box
                                    aria-label={tier.definition.label}
                                    className={classes.pip}
                                    data-completed={tier.completed}
                                    data-unviewed={unviewedTierIds.has(
                                      tier.definition.id,
                                    )}
                                    key={tier.definition.id}
                                  />
                                ))}
                              </Group>
                            ) : null}

                            {showBar && progress ? (
                              <Stack gap={4}>
                                <Box className={classes.bar}>
                                  <Box
                                    className={classes.barFill}
                                    style={{ width: `${percent}%` }}
                                  />
                                </Box>
                                {nearMiss ? (
                                  <Text className={classes.nearMiss}>{nearMiss}</Text>
                                ) : null}
                              </Stack>
                            ) : null}
                          </Stack>
                        </AchievementPopover>

                        {BADGE_ALBUM_LINK_IDS.has(achievement.definition.id) ? (
                          <Group justify="flex-end">
                            <Button
                              className={classes.inlineAction}
                              onClick={onViewBadges}
                              px={8}
                              py={0}
                              variant="subtle"
                            >
                              View badges
                            </Button>
                          </Group>
                        ) : null}
                      </Stack>
                    )
                  })}
                </Stack>
              </Stack>
            )
          })}
        </Stack>
      </ScrollArea>
    </Stack>
  )
}
